import { useCallback } from 'react'
import { useThreadContext } from '../context/ThreadProvider'
import { sendMessage as sendMessageService } from '../services/sendMessage'
import type { SendMessagePayload, Message } from '@standardagents/client'

// Track if we've already warned to avoid spam
let hasWarned = false

/**
 * @deprecated Use `const { sendMessage } = useThread()` instead.
 *
 * Hook that returns a function to send messages to the current thread.
 * Must be used within a ThreadProvider.
 *
 * This hook automatically uses the thread ID from the ThreadProvider context,
 * so you don't need to pass it manually.
 *
 * @returns A function that sends a message to the current thread
 *
 * @throws Error if used outside of ThreadProvider
 *
 * @example
 * ```tsx
 * // NEW - Recommended
 * const { sendMessage } = useThread()
 * await sendMessage({ role: 'user', content: 'Hello!' })
 *
 * // OLD - Deprecated
 * const sendMessage = useSendMessage()
 * await sendMessage({ role: 'user', content: 'Hello!' })
 * ```
 */
export function useSendMessage(): (payload: SendMessagePayload) => Promise<Message> {
  if (!hasWarned && process.env.NODE_ENV !== 'production') {
    hasWarned = true
    console.warn(
      '[DEPRECATED] useSendMessage() is deprecated.\n' +
      'Use: const { sendMessage } = useThread()\n' +
      'Then: await sendMessage({ role: "user", content: "Hello!" })'
    )
  }
  let context: ReturnType<typeof useThreadContext>

  try {
    context = useThreadContext()
  } catch {
    throw new Error('useSendMessage must be used within a ThreadProvider')
  }

  const { threadId } = context

  return useCallback(
    (payload: SendMessagePayload) => {
      return sendMessageService(threadId, payload)
    },
    [threadId]
  )
}
